import React, { useEffect, useContext, useCallback } from 'react';
import { PAGED_INCOME_SOURCES_QUERY } from './queries';
import { INCOME_SOURCE_DELETED } from './subscriptions';
import { ServiceContext } from 'providers/ServiceProvider';
import { useTranslation } from 'react-i18next';
import { Card } from '@totalsoft/rocket-ui';
import { useApolloClient } from '@apollo/client';
import { getListBrowserStore } from '@ts-private/list-browser';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { ExportModalComponent } from 'components/common/export/ExportModalComponent';
import { useToast } from 'hooks/toasts/useToast';
import { useMemo } from 'react';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import { useMutation } from 'hooks/apollo/useMutation';
import { DELETE_INCOME_SOURCE } from './mutations';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import IncomeSourceItem from './IncomeSourceItem';
import useSubscription from 'hooks/apollo/useSubscription';

export const incomeSourcesStore = getListBrowserStore({
  filterDefinitions: [
    {
      name: 'name',
      caption: 'IncomeSources.IncomeName',
      type: 'text'
    },
    {
      name: 'isActive',
      caption: 'IncomeSources.IsActive',
      type: 'boolean'
    }
  ],
  orderByFields: [
    {
      caption: 'IncomeSources.IncomeName',
      value: 'Name'
    }
  ],
  pager: {
    orderBy: 'Name',
    direction: 'asc'
  }
});

const IncomeSourceListBrowser = () => {
  const [t] = useTranslation();
  const addToast = useToast();
  const client = useApolloClient();
  const confirm = useConfirmDialog();

  const [activeService] = useContext(ServiceContext);

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: PAGED_INCOME_SOURCES_QUERY,
        name: 'pagedIncomeSources'
      }
    }),
    []
  );

  // #region Delete

  useSubscription(INCOME_SOURCE_DELETED, {
    onData: ({ data, client }) => {
      addToast(t('IncomeSources.IncomeSourceDeleted'), 'success');
      client.cache.evict({ id: `IncomeSourceItem:${data.data.incomeSourceDeleted.incomeSourceId}` });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedIncomeSources' });
      client.cache.gc();
    }
  });

  const [deleteIncomeSource] = useMutation(DELETE_INCOME_SOURCE);

  const handleDelete = useCallback(
    id => {
      confirm(
        () => {
          deleteIncomeSource({ variables: { input: { id } } });
        },
        {
          content: t('IncomeSources.DeleteIncomeSource')
        }
      );
    },
    [deleteIncomeSource, confirm, t]
  );

  // #endregion

  useEffect(() => {
    if (activeService) {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedIncomeSources' });
      client.cache.gc();
    }
  }, [activeService, client]);

  const itemComponent = useCallback(({ data }) => <IncomeSourceItem data={data} onDelete={handleDelete} />, [handleDelete]);

  const columns = useMemo(
    () => [
      { name: 'orderNo', caption: 'IncomeSources.OrderNo', width: '100px' },
      { name: 'incomeCode', caption: 'IncomeSources.IncomeCode' },
      { name: 'incomeName', caption: 'IncomeSources.IncomeName' },
      { name: 'incomeSourceArea', caption: 'IncomeSources.IncomeSourceArea' },
      { name: 'isActive', caption: 'IncomeSources.IsActive', width: 10, align: 'center' },
      { name: 'actions', caption: 'Actions', width: '120px' }
    ],
    []
  );

  return (
    <Card icon={ListAltIcon}>
      <TableListBrowser name='leasing-commissions-browser' store={incomeSourcesStore} ItemComponent={itemComponent} columns={columns} />
      <ExportModalComponent store={incomeSourcesStore} options={fetchingOptions} />
      <ApolloFetchingComponent store={incomeSourcesStore} options={fetchingOptions} />
    </Card>
  );
};

export default IncomeSourceListBrowser;
